import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BookingProductType, PostProductBookingResponse } from '@products/models/booking.model';
import { ProductBookingService } from '@products/services/product-booking.service';
import { TariffBookingService } from '@products/services/tariff-booking.service';
import { BOOKING_DEBOUNCE_TIME } from '@shared/tokens';
import { ProductBookingActions } from '@store/products/booking/product/product-booking.actions';
import { TariffProductActions } from '@store/products/product.actions';
import { isEmpty, isNil } from 'lodash';
import { catchError, combineLatest, debounceTime, EMPTY, map, of, switchMap } from 'rxjs';

export const setSelfRegistrationTariffBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService)
  ) =>
    actions$.pipe(
      ofType(TariffProductActions.setSelfRegistrationTariffBookings),
      debounceTime(bookingDebounceTime),
      switchMap(({ bookingProductTypes }) => {
        return combineLatest([
          productBookingService.setProductBookings$(bookingProductTypes),
          productBookingService.removeAllProductBookings$()
        ]).pipe(
          switchMap(([addPostProductBookings, { removeBookingProductTypes, removePostProductBookings }]) => {
            const addBookingProductTypes: BookingProductType[] = bookingProductTypes;
            if (isEmpty(addBookingProductTypes)) {
              return EMPTY;
            }

            const postProductBookings = [...addPostProductBookings, ...removePostProductBookings];

            return productBookingService.postProductBookings$(postProductBookings).pipe(
              map((postProductBookingsResponse: PostProductBookingResponse[]) => {
                const bookingProductTypes = [...addBookingProductTypes, ...removeBookingProductTypes];
                const products: BookingProductType[] =
                  productBookingService.mapPostProductBookingsResponseToBookingProductTypes(
                    bookingProductTypes,
                    postProductBookingsResponse
                  );

                return ProductBookingActions.setProductInBookingList(products);
              })
            );
          }),
          catchError(() => of(ProductBookingActions.setProductInBookingList([])))
        );
      })
    ),
  { functional: true }
);

export const removeTariffBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService),
    tariffBookingService = inject(TariffBookingService)
  ) =>
    actions$.pipe(
      ofType(TariffProductActions.removeTariffBooking),
      debounceTime(bookingDebounceTime),
      switchMap(({ ticketPersonId }) => {
        if (isNil(ticketPersonId)) {
          return EMPTY;
        }

        return tariffBookingService.removeTariffBookings$(ticketPersonId).pipe(
          switchMap(({ bookingTariffTypes, removePostProductBookings }) => {
            return productBookingService.postProductBookings$(removePostProductBookings).pipe(
              map((postProductBookingsResponse) => {
                const products: BookingProductType[] =
                  productBookingService.mapPostProductBookingsResponseToBookingProductTypes(
                    bookingTariffTypes,
                    postProductBookingsResponse
                  );

                return ProductBookingActions.removeProductFromBookingList(products);
              })
            );
          }),
          catchError(() => of(ProductBookingActions.removeProductFromBookingList([])))
        );
      })
    ),
  { functional: true }
);
